/**
 * Recover a tab left holding a superseded build.
 *
 * The frontend ships as an nginx image containing exactly one build, so a deploy
 * replaces every hashed asset at once. A tab opened before the deploy still has
 * the old index.html, and its next lazy import asks for a chunk that no longer
 * exists — webpack raises ChunkLoadError and React tears the tree down. Nothing
 * is wrong with the code; the tab simply needs the new build.
 *
 * Reloading fetches it. The marker keeps that from becoming a loop: if a chunk
 * is genuinely missing rather than merely stale, the second failure is left
 * alone so it still reaches Sentry and the boundary still renders.
 */

const RELOAD_MARKER = 'app:stale-bundle-reload';

// Long enough to cover the reload and first render, short enough that a later
// deploy in the same tab is still recovered rather than shown as an error.
const RETRY_WINDOW_MS = 30_000;

const CHUNK_FAILURE = /Loading (?:CSS )?chunk \S+ failed/i;

/** True for the webpack error a tab raises when its build is no longer served. */
export function isStaleBundleError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const { name, message } = error as { name?: unknown; message?: unknown };

  if (name === 'ChunkLoadError') return true;

  return typeof message === 'string' && CHUNK_FAILURE.test(message);
}

// null means "never reloaded". Collapsing that to 0 would compare against the
// epoch and refuse the very first reload, which is the one that matters.
function lastReloadAt(): number | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER);

    return raw === null ? null : Number(raw) || 0;
  } catch {
    // Blocked storage costs us only the loop guard, so treat it as never reloaded.
    return null;
  }
}

function markReloaded(at: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER, String(at));
  } catch {
    // See lastReloadAt: recovery still matters more than the guard.
  }
}

/** Claim the single reload allowed per window; false once one has been spent. */
export function claimStaleBundleReload(now: number = Date.now()): boolean {
  const previous = lastReloadAt();

  if (previous !== null && now - previous < RETRY_WINDOW_MS) return false;
  markReloaded(now);

  return true;
}

/** Reload for a superseded build. False when a reload already failed to help. */
export function recoverFromStaleBundle(): boolean {
  if (!claimStaleBundleReload()) return false;
  window.location.reload();

  return true;
}
